import * as React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import {
    AlertDialog,
    AlertDialogContent,
    Button,
    Dialog,
    DialogContent,
    TextArea,
} from "@jhool-io/fe-components";
import useToast from "../../../../hooks/useToast";
import {
    useDoesUserHavePermission,
    useDoesUserHaveRole,
} from "../../../../hooks/permissions";
import { useGenerateAiSessionNoteDraft } from "../../../../hooks/mutations/note";
import { UserPermisions, UserRole } from "../../../../utils/types/user";
import {
    IAiSessionNoteDraft,
    IAiSessionNoteWriterContext,
} from "../../types/notes.types";
import {
    AiGeneratedNoteField,
    formatAiWriterFieldLabel,
    hasWriterContext,
    isAiSessionNotesEnabled,
} from "../../helpers/aiSessionNotes";
import AiGeneratingRobot from "../AiGeneratingRobot/AiGeneratingRobot";

interface WriterFormValues {
    sessionSynopsis: string;
}

interface WriterInput {
    sessionSynopsis: string;
    noteContext: IAiSessionNoteWriterContext;
}

export interface AiSessionNoteWriterControls {
    getCanOpen(): boolean;
    isActive: boolean;
    isGenerating: boolean;
    openDialog(): void;
}

export interface AiSessionNoteWriterProps {
    noteId: string;
    getNoteContext(): IAiSessionNoteWriterContext;
    getExistingContentFields(): AiGeneratedNoteField[];
    onApplyDraft(
        draft: IAiSessionNoteDraft,
        noteContext: IAiSessionNoteWriterContext
    ): void;
    renderTrigger?(controls: AiSessionNoteWriterControls): React.ReactNode;
}

interface AiSessionNoteWriterFormProps extends AiSessionNoteWriterProps {
    isCurrentNote(): boolean;
}

const defaultFormValues: WriterFormValues = {
    sessionSynopsis: "",
};

const minimumSynopsisLength = 100;

const writerSchema = yup.object({
    sessionSynopsis: yup
        .string()
        .trim()
        .required("Session synopsis is required")
        .min(
            minimumSynopsisLength,
            [
                "Session synopsis must contain at least",
                `${minimumSynopsisLength} characters`,
            ].join(" ")
        ),
});

function AiSessionNoteWriterForm({
    noteId,
    getNoteContext,
    getExistingContentFields,
    onApplyDraft,
    isCurrentNote,
    renderTrigger,
}: AiSessionNoteWriterFormProps) {
    const [isOpen, setIsOpen] = React.useState(false);
    const [showOverwriteConfirmation, setShowOverwriteConfirmation] =
        React.useState(false);
    const [existingContentFields, setExistingContentFields] = React.useState<
        AiGeneratedNoteField[]
    >([]);
    const sourceContextRef = React.useRef<IAiSessionNoteWriterContext>({
        diagnosis_codes: [],
    });
    const pendingInputRef = React.useRef<WriterInput | null>(null);
    const generateDraft = useGenerateAiSessionNoteDraft(noteId);
    const { toast } = useToast();
    const canUseAi = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_NOTES_AI_REVIEW,
    ]);
    const isClinician = useDoesUserHaveRole([
        UserRole.PROVIDER,
        UserRole.SUPER_ADMIN,
    ]);
    const {
        control,
        clearErrors,
        getValues,
        handleSubmit,
        reset,
        trigger,
        formState: { errors, isValid },
    } = useForm<WriterFormValues>({
        resolver: yupResolver(writerSchema),
        defaultValues: defaultFormValues,
        mode: "onChange",
    });

    if (!isAiSessionNotesEnabled() || !canUseAi || !isClinician) return null;

    const openDialog = () => {
        const currentContext = getNoteContext();
        if (!hasWriterContext(currentContext)) {
            toast({
                mode: "error",
                message:
                    "Mantle AI needs a treatment goal from this note or a " +
                    "previous note before it can generate a draft.",
            });
            return;
        }
        sourceContextRef.current = {
            ...currentContext,
            diagnosis_codes: [...currentContext.diagnosis_codes],
        };
        const retainedSynopsis = getValues("sessionSynopsis");
        reset({
            sessionSynopsis: retainedSynopsis,
        });
        if (retainedSynopsis) trigger("sessionSynopsis");
        pendingInputRef.current = null;
        setExistingContentFields([]);
        setShowOverwriteConfirmation(false);
        setIsOpen(true);
    };

    const closeDialog = () => {
        if (generateDraft.isLoading) return;
        pendingInputRef.current = null;
        setIsOpen(false);
        setShowOverwriteConfirmation(false);
        setExistingContentFields([]);
        clearErrors();
    };

    const runWriter = (input: WriterInput) => {
        generateDraft.mutate(
            {
                session_synopsis: input.sessionSynopsis,
                note_context: input.noteContext,
            },
            {
                onSuccess: (response) => {
                    if (!isCurrentNote()) return;
                    onApplyDraft(response.data, input.noteContext);
                    toast({
                        mode: "success",
                        message:
                            "Draft added. Review and edit it, then run Mantle AI review " +
                            "before signing.",
                    });
                    pendingInputRef.current = null;
                    setIsOpen(false);
                    setShowOverwriteConfirmation(false);
                    setExistingContentFields([]);
                    clearErrors();
                },
                onError: (error) => {
                    if (!isCurrentNote()) return;
                    toast({
                        mode: "error",
                        message:
                            error.response?.data.message ??
                            "Could not generate a draft. Your note was not changed.",
                    });
                },
            }
        );
    };

    const buildWriterInput = (values: WriterFormValues): WriterInput => ({
        sessionSynopsis: values.sessionSynopsis.trim(),
        noteContext: sourceContextRef.current,
    });

    const handleGenerate = handleSubmit((values) => {
        const input = buildWriterInput(values);
        const populatedFields = getExistingContentFields();
        if (populatedFields.length > 0) {
            pendingInputRef.current = input;
            setExistingContentFields(populatedFields);
            setShowOverwriteConfirmation(true);
            return;
        }
        runWriter(input);
    });

    return (
        <>
            {renderTrigger ? (
                renderTrigger({
                    getCanOpen: () => hasWriterContext(getNoteContext()),
                    isActive:
                        isOpen ||
                        showOverwriteConfirmation ||
                        generateDraft.isLoading,
                    isGenerating: generateDraft.isLoading,
                    openDialog,
                })
            ) : (
                <Button type="button" onClick={openDialog}>
                    Generate draft with Mantle AI
                </Button>
            )}
            <Dialog open={isOpen}>
                <DialogContent
                    title="Generate session note draft"
                    variant="center"
                    showFooter={false}
                    handleCloseDialog={closeDialog}
                >
                    <form
                        className="flex flex-col gap-4"
                        onSubmit={handleGenerate}
                        aria-label="generate session note draft"
                        noValidate
                    >
                        <p className="text-sm text-textSec">
                            Describe the session, including client reports,
                            observations, interventions, response, progress, and
                            next steps. Include only clinically relevant
                            details. Mantle AI drafts the note; diagnoses stay
                            clinician-selected. Review before saving.
                        </p>
                        <Controller
                            name="sessionSynopsis"
                            control={control}
                            render={({ field }) => {
                                const characterCount =
                                    field.value.trim().length;
                                return (
                                    <div>
                                        <TextArea
                                            label="Session synopsis"
                                            value={field.value}
                                            onChange={field.onChange}
                                            onBlur={field.onBlur}
                                            maxLength={20000}
                                            rows={8}
                                            isRequired
                                            hasError={Boolean(
                                                errors.sessionSynopsis
                                            )}
                                            errorText={
                                                errors.sessionSynopsis?.message
                                            }
                                            disabled={generateDraft.isLoading}
                                        />
                                        <p className="mt-1 text-xs text-textSec">
                                            {characterCount.toLocaleString()} /{" "}
                                            {minimumSynopsisLength}
                                            {" minimum characters"}
                                        </p>
                                    </div>
                                );
                            }}
                        />
                        <div
                            data-testid="ai-writer-action-bar"
                            className={[
                                "sticky -bottom-[--padding-card-y-lg] z-[2]",
                                "-mx-[--padding-card-x-lg] flex justify-end gap-3",
                                "border-t border-stroke bg-[var(--card-bg)] text-text",
                                "px-[--padding-card-x-lg] pt-4",
                                "pb-[max(var(--padding-card-y-lg),env(safe-area-inset-bottom))]",
                            ].join(" ")}
                        >
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={closeDialog}
                                disabled={generateDraft.isLoading}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={generateDraft.isLoading || !isValid}
                            >
                                {generateDraft.isLoading ? (
                                    <span
                                        role="status"
                                        className="inline-flex items-center gap-2"
                                    >
                                        <AiGeneratingRobot />
                                        Generating…
                                    </span>
                                ) : (
                                    "Generate draft"
                                )}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
            <AlertDialog open={showOverwriteConfirmation}>
                <AlertDialogContent
                    title="Update existing note content?"
                    description={[
                        `Mantle AI may update: ${existingContentFields
                            .map(formatAiWriterFieldLabel)
                            .join(", ")}.`,
                        "Only fields with new content will be updated; empty results keep",
                        "current values. Diagnoses, Causing, Intention for session, and administrative",
                        "fields will not change.",
                    ].join(" ")}
                    saveText="Generate and update"
                    cancelText="Keep current content"
                    onCancelClick={() => {
                        pendingInputRef.current = null;
                        setShowOverwriteConfirmation(false);
                    }}
                    onSaveClick={() => {
                        setShowOverwriteConfirmation(false);
                        const input = pendingInputRef.current;
                        pendingInputRef.current = null;
                        if (input) runWriter(input);
                    }}
                    isSaveBtnDisabled={generateDraft.isLoading}
                    isCancelBtnDisabled={generateDraft.isLoading}
                />
            </AlertDialog>
        </>
    );
}

export default function AiSessionNoteWriter({
    noteId,
    ...writerProps
}: AiSessionNoteWriterProps) {
    const activeNoteIdRef = React.useRef(noteId);
    activeNoteIdRef.current = noteId;

    return (
        <AiSessionNoteWriterForm
            key={noteId}
            noteId={noteId}
            {...writerProps}
            isCurrentNote={() => activeNoteIdRef.current === noteId}
        />
    );
}
